/**
 * Composer · B2C mode.
 *
 * Two fields, both lean. Name is required; phone is optional. State
 * of supply is intentionally NOT collected here — it's a Section-31
 * CGST Rules concern that only kicks in for invoices over Rs 2.5L,
 * so we ask at invoice composer time, not customer creation time.
 *
 * Address is omitted on purpose. For B2C the address rarely matters
 * (most B2C invoices are retail / walk-in). If it does turn out to
 * matter later, the user adds it from the customer detail page via
 * the InlineAddButton pattern.
 */
import * as React from "react";
import { cn } from "@flint/ui";
import { Info } from "lucide-react";

export interface ComposerB2CState {
  name: string;
  phone: string;
}

export interface ComposerB2CProps {
  state: ComposerB2CState;
  onChange: (patch: Partial<ComposerB2CState>) => void;
}

export function ComposerB2C({ state, onChange }: ComposerB2CProps) {
  return (
    <section
      aria-label="Indian consumer customer"
      className="rounded-md border border-line bg-paper shadow-card"
    >
      <header className="px-[18px] pt-3.5 pb-2 border-b border-line">
        <h3 className="text-sm font-semibold text-ink m-0">Customer details</h3>
        <p className="text-xs text-muted mt-0.5 leading-snug">
          B2C customers don&apos;t need a GSTIN. We&apos;ll ask for state-of-supply only at invoice
          time — and only if the invoice is over Rs 2.5L (Section 31, CGST Rules).
        </p>
      </header>

      <div className="px-[18px] py-[18px] flex flex-col gap-3.5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <Field
            label="Name"
            required
            value={state.name}
            placeholder="e.g. Rohit Sharma"
            onChange={(v) => onChange({ name: v })}
            autoFocus
          />
          <PhoneField
            label="Phone"
            optionalSub="optional"
            value={state.phone}
            onChange={(v) => onChange({ phone: v })}
            hint="Used for WhatsApp invoice + receipt delivery. Skip if cash-and-carry."
          />
        </div>

        <div
          className={cn(
            "flex items-start gap-2 px-3 py-2.5 rounded-md",
            "bg-sky-50 border border-sky-100 text-sky-700 text-xs leading-snug",
          )}
        >
          <Info className="w-3.5 h-3.5 mt-px flex-shrink-0 text-sky-600" />
          <div>
            <strong className="font-semibold">One field, that&apos;s it.</strong> Address, state,
            GSTIN are only required when a customer crosses certain invoice thresholds — Flint asks
            for them at that moment, not here.
          </div>
        </div>
      </div>
    </section>
  );
}

interface FieldProps {
  label: string;
  required?: boolean;
  optionalSub?: string;
  value: string;
  placeholder: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  autoComplete?: string;
  hint?: string;
  autoFocus?: boolean;
  onChange: (next: string) => void;
}

function Field({
  label,
  required,
  optionalSub,
  value,
  placeholder,
  inputMode,
  autoComplete,
  hint,
  autoFocus,
  onChange,
}: FieldProps) {
  return (
    <label className="flex flex-col gap-1 min-w-0">
      <span className="text-xs font-semibold text-ink-2">
        {label}
        {required ? <span className="text-bad ml-0.5">*</span> : null}
        {optionalSub ? <span className="text-muted font-medium ml-1">({optionalSub})</span> : null}
      </span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        inputMode={inputMode}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-9 px-3 rounded-md border border-line bg-paper text-sm text-ink",
          "placeholder:text-muted",
          "hover:border-line-2",
          "focus:outline-none focus:border-sky-600 focus:ring-[3px] focus:ring-sky-100",
        )}
      />
      {hint ? <span className="text-xs text-muted leading-snug">{hint}</span> : null}
    </label>
  );
}

interface PhoneFieldProps {
  label: string;
  optionalSub?: string;
  value: string;
  hint?: string;
  onChange: (next: string) => void;
}

/**
 * Phone input with a visual `+91` prefix. The stored value is always
 * the bare 10-digit national number — matching the contract's
 * `phoneIN` regex (`/^[6-9]\d{9}$/`). Pasting `+91 98765 …` or
 * `919876543210` is normalised on input; the user can't end up with
 * a value the server will reject.
 */
function PhoneField({ label, optionalSub, value, hint, onChange }: PhoneFieldProps) {
  const display = React.useMemo(() => {
    // 5-5 grouping for legibility while typing.
    if (!value) return "";
    return value.length > 5 ? `${value.slice(0, 5)} ${value.slice(5)}` : value;
  }, [value]);

  const handleChange = (raw: string) => {
    // Strip everything that isn't a digit, drop a leading `91` if the
    // user pasted with country code, then clamp to the last 10 digits.
    const digits = raw.replace(/\D/g, "");
    const trimmed = digits.startsWith("91") && digits.length > 10 ? digits.slice(2) : digits;
    onChange(trimmed.slice(0, 10));
  };

  return (
    <label className="flex flex-col gap-1 min-w-0">
      <span className="text-xs font-semibold text-ink-2">
        {label}
        {optionalSub ? <span className="text-muted font-medium ml-1">({optionalSub})</span> : null}
      </span>
      <div
        className={cn(
          "flex items-stretch h-9 rounded-md overflow-hidden",
          "border border-line bg-paper transition-colors",
          "hover:border-line-2",
          "focus-within:border-sky-600 focus-within:ring-[3px] focus-within:ring-sky-100",
        )}
      >
        <span
          aria-hidden
          className="inline-flex items-center px-2.5 text-ink-2 border-r border-line bg-bg font-mono text-sm tabular-nums"
        >
          +91
        </span>
        <input
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          value={display}
          placeholder="98765 43210"
          onChange={(e) => handleChange(e.target.value)}
          className="flex-1 min-w-0 px-3 bg-transparent text-sm text-ink placeholder:text-muted focus:outline-none"
        />
      </div>
      {hint ? <span className="text-xs text-muted leading-snug">{hint}</span> : null}
    </label>
  );
}
